import { CommonModule } from '@angular/common';
import { afterNextRender, Component, ElementRef, HostListener, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { debounceTime, distinctUntilChanged, filter, map, of, Subject, Subscription, switchMap, take } from 'rxjs';
import { EmployeeInfoButtonComponent } from '../../shared/components/employee-info-button/employee-info-button.component';
import { SelectOnFocusDirective } from '../../shared/directives/select-on-focus.directive';
import { EmployeeIdModalComponent, EmployeeIdModalData } from '../../shared/modals/employee-id-modal/employee-id-modal.component';
import { PasteTextBlockModalComponent } from '../../shared/modals/paste-text-block-modal/paste-text-block-modal.component';
import { DeliveryStatusEnum, IDeliveryNote, IDeliveryNotePosition, ShippingTypeEnum } from '../../shared/models/delivery-note.model';
import { IOrder } from '../../shared/models/order.model';
import { TextBlockType } from '../../shared/models/text-block.model';
import { IUser, UserRole } from '../../shared/models/user.model';
import { AuthService } from '../../shared/services/auth.service';
import { DeliveryNoteLockService, IDeliveryNoteLock } from '../../shared/services/delivery-note-lock.service';
import { DeliveryNoteService } from '../../shared/services/delivery-note.service';
import { ModalService } from '../../shared/services/modal.service';
import { OrderService } from '../../shared/services/order.service';
import { ToastService } from '../../shared/services/toast.service';
import { TextFieldModule } from '@angular/cdk/text-field';
@Component({
  selector: 'app-create-delivery-note',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectModule, SelectOnFocusDirective, TextFieldModule, EmployeeInfoButtonComponent],
  templateUrl: './create-delivery-note.component.html',
  styleUrls: ['./create-delivery-note.component.scss'],
})
export class CreateDeliveryNoteComponent implements OnInit, OnDestroy {
  public deliveryNoteForm!: FormGroup;
  public isEditMode = false;
  public isLoading = false;
  public isSubmitting = false;
  public deliveryNoteId: string | null = null;
  private noteNumber: string | null = null;
  public orders: IOrder[] = [];

  public orderSearchValue = '';
  public showOrderDropdown = false;

  // Text insertion tracking
  private currentFocusedElement: HTMLTextAreaElement | HTMLInputElement | null = null;

  public filteredOrders: IOrder[] = [];
  public highlightedOrderIndex = -1;
  public isOrderSearchLoading = false;
  public readonly minOrderSearchLength = 3;

  private orderSearchTerms = new Subject<string>();
  private subscriptions = new Subscription();

  private isSelectingOrder = false;

  // Header pin state
  public isHeaderPinned = false;

  public isLockLoading = false;
  public isLocked = false;
  public lockInfo: IDeliveryNoteLock | null = null;
  public isExporting = false;
  public currentUser: IUser | null = null;
  private currentUserId: string | null = null;
  public UserRole = UserRole;
  private confirmedStatus: DeliveryStatusEnum = DeliveryStatusEnum.DELIVERYNOTEQSCREATED;
  // True while the Mitarbeiternummer modal is shown, so leaving the page (e.g. browser back) closes it.
  private isEmployeeIdModalOpen = false;

  public DeliveryStatusEnum = DeliveryStatusEnum;

  public statusOptions = [
    { value: DeliveryStatusEnum.DELIVERYNOTEQSCREATED, label: 'QS - Lieferzettel erstellt' },
    { value: DeliveryStatusEnum.READYFORDELIVERY, label: 'Versandbereit' },
    { value: DeliveryStatusEnum.DELIVERYNOTECREATED, label: 'Lieferschein erstellt' },
    { value: DeliveryStatusEnum.DELIVERED, label: 'Ausgeliefert' },
  ];

  public shippingTypeOptions = [
    { value: ShippingTypeEnum.PARCELDELIVERY, label: 'Paketversand' },
    { value: ShippingTypeEnum.PALETTEDELIVERY, label: 'Palettenversand' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private el: ElementRef,
    private deliveryNoteService: DeliveryNoteService,
    private deliveryNoteLockService: DeliveryNoteLockService,
    private orderService: OrderService,
    private authService: AuthService,
    private modalService: ModalService,
    private toastService: ToastService,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.initForm();

    this.subscriptions.add(
      this.orderSearchTerms
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
          switchMap(term => {
            const q = (term || '').trim();
            if (q.length < this.minOrderSearchLength) {
              this.isOrderSearchLoading = false;
              return of<IOrder[]>([]);
            }
            this.isOrderSearchLoading = true;
            return this.orderService.getOrders(1, 10, q, 'internalOrderId', 'asc').pipe(
              map(response => response.DATA?.data || []),
              map(results => results || [])
            );
          })
        )
        .subscribe({
          next: results => {
            this.orders = results;
            this.filteredOrders = results;
            this.isOrderSearchLoading = false;
            this.highlightedOrderIndex = this.filteredOrders.length > 0 ? 0 : -1;
          },
          error: () => {
            this.isOrderSearchLoading = false;
            this.orders = [];
            this.filteredOrders = [];
            this.highlightedOrderIndex = -1;
            console.error('Error searching orders');
          },
        })
    );

    // Subscribe to get current user ID for lock
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        if (user) {
          this.currentUserId = user._id || '';
          this.currentUser = user;
        }
      })
    );

    // Check if we're in edit mode
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.deliveryNoteId = id;
      this.acquireLock(id);
    } else {
      // Add first empty position for new delivery notes
      this.addPosition();
      // Ask who is creating the note. After the first render, so the modal isn't created in the
      // middle of this page's change detection.
      afterNextRender(() => this.confirmEmployeeId(), { injector: this.injector });
    }
  }

  // Accounts are shared, so the creator confirms their own Mitarbeiternummer (prefilled with the
  // account's). The modal validates it against the backend before it closes; Abbrechen leaves the page,
  // since a Lieferzettel can't be created without it.
  private confirmEmployeeId(): void {
    this.subscriptions.add(
      this.authService.currentUser$
        .pipe(
          filter((user): user is IUser => !!user?._id),
          take(1)
        )
        .subscribe(user => {
          const data: EmployeeIdModalData = { employeeId: user.employeeId?.toString() || '' };
          this.isEmployeeIdModalOpen = true;
          this.modalService
            .open(EmployeeIdModalComponent, data)
            .then((employeeId: string | null) => {
              this.isEmployeeIdModalOpen = false;
              if (employeeId) {
                this.deliveryNoteForm.patchValue({ employeeId });
              } else {
                this.cancel();
              }
            })
            .catch(() => {});
        })
    );
  }

  private acquireLock(deliveryNoteId: string): void {
    this.isLockLoading = true;

    this.subscriptions.add(
      this.authService.currentUser$
        .pipe(
          filter((user): user is IUser => !!user?._id),
          take(1)
        )
        .subscribe(user => {
          this.deliveryNoteLockService.createLock(deliveryNoteId, String(user.employeeId || '')).subscribe({
            next: () => {
              this.isLocked = false;
              this.lockInfo = null;
              this.isLockLoading = false;
              this.loadDeliveryNote(deliveryNoteId);
            },
            error: error => {
              this.isLocked = true;
              this.lockInfo = error.error?.DATA || null;
              this.isLockLoading = false;
              this.loadDeliveryNote(deliveryNoteId);
            },
          });
        })
    );
  }

  private releaseLock(): void {
    if (!this.isEditMode || !this.deliveryNoteId || this.isLocked) return;

    this.deliveryNoteLockService.deleteLock(this.deliveryNoteId).subscribe();
  }

  private initForm(): void {
    this.deliveryNoteForm = this.fb.group({
      order: [null, Validators.required],
      employeeId: ['', Validators.required],
      shippingType: [ShippingTypeEnum.PARCELDELIVERY, Validators.required],
      status: [DeliveryStatusEnum.DELIVERYNOTEQSCREATED, Validators.required],
      positions: this.fb.array([]),
      additionalInfo: [''],
    });
  }

  private loadDeliveryNote(id: string): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.deliveryNoteService.getDeliveryNoteById(id).subscribe({
        next: response => {
          if (response.DATA) {
            this.patchFormValues(response.DATA);
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.router.navigate(['/delivery-notes']);
        },
      })
    );
  }

  private patchFormValues(deliveryNote: IDeliveryNote): void {
    this.noteNumber = deliveryNote.noteNumber ?? null;
    this.confirmedStatus = deliveryNote.status;

    this.deliveryNoteForm.patchValue({
      order: deliveryNote.order,
      employeeId: deliveryNote.employeeId,
      shippingType: deliveryNote.shippingType || ShippingTypeEnum.PARCELDELIVERY,
      status: deliveryNote.status,
      additionalInfo: deliveryNote.additionalInfo || '',
    });

    if (deliveryNote.order) {
      this.orderSearchValue = this.getOrderLabel(deliveryNote.order as unknown as IOrder);
    }

    // Clear existing positions and add from delivery note
    this.positions.clear();
    if (deliveryNote.positions && deliveryNote.positions.length > 0) {
      deliveryNote.positions.forEach(pos => {
        this.positions.push(this.createPositionGroup(pos));
      });
    } else {
      this.addPosition();
    }
  }

  // Only admins see the info icon that reveals the name behind the Mitarbeiter-Nr.
  public get isAdmin(): boolean {
    return this.currentUser?.role === UserRole.ADMIN;
  }

  public get positions(): FormArray {
    return this.deliveryNoteForm.get('positions') as FormArray;
  }

  public createPositionGroup(position?: Partial<IDeliveryNotePosition>): FormGroup {
    const group = this.fb.group({
      articlePosition: [position?.articlePosition || '', Validators.required], // Manual position entry
      rollCount: [position?.rollCount || 0, [Validators.required, Validators.min(0)]],
      piecesPerRoll: [position?.piecesPerRoll || 0, [Validators.required, Validators.min(0)]],
      totalPieces: [{ value: position?.totalPieces || 0, disabled: true }],
      folie1: [position?.folie1 || 0, [Validators.min(0)]],
      folieN: [position?.folieN || 0, [Validators.min(0)]],
      folieA: [position?.folieA || 0, [Validators.min(0)]],
      dryP: [position?.dryP || 0, [Validators.min(0)]],
      umCarton: [position?.umCarton || 0, [Validators.min(0)]],
      ammopack: [position?.ammopack || 0, [Validators.min(0)]],
      cartonCount: [position?.cartonCount || 0, [Validators.min(0)]],
      weightKg: [position?.weightKg || 0, [Validators.min(0)]],
      note: [position?.note || ''],
      deliveryCheckout: [position?.deliveryCheckout ?? undefined],
    });

    // Subscribe to changes to calculate totalPieces
    const rollSub = group.get('rollCount')?.valueChanges.subscribe(() => this.calculateTotalPieces(group));
    const piecesSub = group.get('piecesPerRoll')?.valueChanges.subscribe(() => this.calculateTotalPieces(group));
    if (rollSub) this.subscriptions.add(rollSub);
    if (piecesSub) this.subscriptions.add(piecesSub);

    return group;
  }

  private calculateTotalPieces(group: FormGroup): void {
    const rollCount = group.get('rollCount')?.value || 0;
    const piecesPerRoll = group.get('piecesPerRoll')?.value || 0;
    group.get('totalPieces')?.setValue(rollCount * piecesPerRoll);
  }

  addPosition(): void {
    this.positions.push(this.createPositionGroup());
  }

  // "Zeile hinzufügen": adds a row and focuses its first field, so the user can type right away.
  public addPositionAndFocus(): void {
    this.addPosition();

    // The new row doesn't exist in the DOM yet — wait for it to render.
    const index = this.positions.length - 1;
    afterNextRender(
      () => {
        const row = (this.el.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('tbody tr')[index];
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row?.querySelector<HTMLElement>('[formcontrolname="articlePosition"]')?.focus({ preventScroll: true });
      },
      { injector: this.injector }
    );
  }

  public toggleHeaderPin(): void {
    this.isHeaderPinned = !this.isHeaderPinned;
    const main = document.querySelector('main') as HTMLElement | null;
    if (main) {
      main.style.overflow = this.isHeaderPinned ? 'hidden' : '';
    }

    if (this.isHeaderPinned) {
      const thead = this.el.nativeElement.querySelector('.table-wrapper');
      thead?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public removePosition(index: number): void {
    if (this.positions.length > 1) {
      this.positions.removeAt(index);
    }
  }

  public get totalRollCount(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('rollCount')?.value || 0), 0);
  }

  public isDuplicateArticlePosition(index: number): boolean {
    const value = this.positions.at(index)?.get('articlePosition')?.value;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key) return false;

    let count = 0;
    for (const ctrl of this.positions.controls) {
      const v = ctrl.get('articlePosition')?.value;
      const k = v === null || v === undefined ? '' : String(v).trim();
      if (k && k === key) count++;
      if (count > 1) return true;
    }
    return false;
  }

  public isLastOccurrenceArticlePosition(index: number): boolean {
    const value = this.positions.at(index)?.get('articlePosition')?.value;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key) return false;

    if (!this.isDuplicateArticlePosition(index)) return false;

    for (let i = index + 1; i < this.positions.length; i++) {
      const v = this.positions.at(i)?.get('articlePosition')?.value;
      const k = v === null || v === undefined ? '' : String(v).trim();
      if (k && k === key) return false;
    }

    return true;
  }

  public getTotalPiecesForArticlePosition(index: number): number {
    const value = this.positions.at(index)?.get('articlePosition')?.value;
    const key = value === null || value === undefined ? '' : String(value).trim();
    if (!key) return 0;

    return this.positions.controls.reduce((sum, ctrl) => {
      const v = ctrl.get('articlePosition')?.value;
      const k = v === null || v === undefined ? '' : String(v).trim();
      if (!k || k !== key) return sum;

      const totalPosPieces = ctrl.get('totalPieces')?.value || 0;

      return sum + totalPosPieces;
    }, 0);
  }

  public get totalFolie1(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('folie1')?.value || 0), 0);
  }

  public get totalFolieN(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('folieN')?.value || 0), 0);
  }

  public get totalFolieA(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('folieA')?.value || 0), 0);
  }

  public get totalDryP(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('dryP')?.value || 0), 0);
  }

  public get totalUmCarton(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('umCarton')?.value || 0), 0);
  }

  public get totalAmmopack(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('ammopack')?.value || 0), 0);
  }

  public get totalCartonCount(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('cartonCount')?.value || 0), 0);
  }

  public get totalWeight(): number {
    return this.positions.controls.reduce((sum, control) => sum + (control.get('weightKg')?.value * control.get('cartonCount')?.value || 0), 0);
  }

  public cancel(): void {
    this.router.navigate(['/delivery-notes']);
  }

  public onStatusChange(newValue: DeliveryStatusEnum): void {
    if (newValue === DeliveryStatusEnum.DELIVERED && !this.allPositionsChecked()) {
      this.toastService.showWarning('Bitte zuerst alle Positionen überprüfen');
      this.deliveryNoteForm.get('status')!.setValue(this.confirmedStatus, { emitEvent: false });
      return;
    }
    this.confirmedStatus = newValue;
  }

  private allPositionsChecked(): boolean {
    return this.positions.controls.every(ctrl => ctrl.get('deliveryCheckout')?.value?.checked === true);
  }

  public submit(): void {
    if (this.deliveryNoteForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    // In edit mode, check lock before saving
    if (this.isEditMode && this.deliveryNoteId && this.currentUserId) {
      this.isSubmitting = true;
      this.subscriptions.add(
        this.deliveryNoteLockService.createLock(this.deliveryNoteId, String(this.currentUser?.employeeId || '')).subscribe({
          next: () => {
            this.isLocked = false;
            this.lockInfo = null;
            this.save();
          },
          error: error => {
            this.isLocked = true;
            this.lockInfo = error.error?.DATA || null;
            this.isSubmitting = false;
          },
        })
      );
    } else {
      this.isSubmitting = true;
      this.save();
    }
  }

  private save(): void {
    // Get form values and include totalPieces from disabled fields
    const formValue = this.deliveryNoteForm.getRawValue();

    // Calculate totalPieces for each position
    formValue.positions = formValue.positions.map((pos: IDeliveryNotePosition, index: number) => ({
      ...pos,
      position: index + 1,
      totalPieces: pos.rollCount * pos.piecesPerRoll,
    }));

    if (this.isEditMode && this.deliveryNoteId) {
      this.subscriptions.add(
        this.deliveryNoteService.updateDeliveryNote(this.deliveryNoteId, formValue).subscribe({
          next: () => {
            this.isSubmitting = false;
            this.toastService.showSuccess('Lieferzettel erfolgreich aktualisiert');
          },
          error: () => {
            this.isSubmitting = false;
            this.toastService.showError('Fehler beim Aktualisieren des Lieferzettels');
          },
        })
      );
    } else {
      this.subscriptions.add(
        this.deliveryNoteService.createDeliveryNote(formValue).subscribe({
          next: response => {
            this.isSubmitting = false;
            this.isEditMode = true;
            this.deliveryNoteId = response.DATA._id || null;
          },
          error: () => {
            this.isSubmitting = false;
            this.toastService.showError('Fehler beim Erstellen des Lieferzettels');
          },
        })
      );
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.deliveryNoteForm.controls).forEach(key => {
      const control = this.deliveryNoteForm.get(key);
      control?.markAsTouched();
      if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            Object.keys(c.controls).forEach(k => c.get(k)?.markAsTouched());
          }
        });
      }
    });
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.deliveryNoteForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  public isPositionFieldInvalid(index: number, fieldName: string): boolean {
    const field = this.positions.at(index)?.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  public compareOrders(o1: IOrder | null, o2: IOrder | null): boolean {
    return o1?._id === o2?._id;
  }

  public openOrderDropdown(): void {
    this.showOrderDropdown = true;
    const q = (this.orderSearchValue || '').trim();
    if (q.length >= this.minOrderSearchLength) {
      this.orderSearchTerms.next(q);
    } else {
      this.orders = [];
      this.filteredOrders = [];
      this.highlightedOrderIndex = -1;
      this.isOrderSearchLoading = false;
    }
  }

  public closeOrderDropdown(): void {
    this.showOrderDropdown = false;
    this.highlightedOrderIndex = -1;
  }

  public onOrderBlur(): void {
    setTimeout(() => {
      if (!this.isSelectingOrder) {
        this.closeOrderDropdown();
      }
      this.isSelectingOrder = false;
    }, 150);
  }

  @HostListener('document:click')
  public onDocumentClick(): void {
    this.closeOrderDropdown();
  }

  public onOrderSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.orderSearchValue = value;
    this.showOrderDropdown = true;

    const q = (value || '').trim();
    if (q.length >= this.minOrderSearchLength) {
      this.orderSearchTerms.next(q);
    } else {
      this.orders = [];
      this.filteredOrders = [];
      this.highlightedOrderIndex = -1;
      this.isOrderSearchLoading = false;
    }

    const selected = this.deliveryNoteForm.get('order')?.value as IOrder | null;
    if (selected && this.getOrderLabel(selected) !== value) {
      this.deliveryNoteForm.patchValue({ order: null });
    }
  }

  public onOrderSearchKeydown(event: KeyboardEvent): void {
    if (!this.showOrderDropdown && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      this.openOrderDropdown();
      event.preventDefault();
      return;
    }

    if (!this.showOrderDropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.filteredOrders.length === 0) return;
      this.highlightedOrderIndex = Math.min(this.highlightedOrderIndex + 1, this.filteredOrders.length - 1);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredOrders.length === 0) return;
      this.highlightedOrderIndex = Math.max(this.highlightedOrderIndex - 1, 0);
    }

    if (event.key === 'Enter') {
      if (this.highlightedOrderIndex >= 0 && this.highlightedOrderIndex < this.filteredOrders.length) {
        event.preventDefault();
        this.selectOrder(this.filteredOrders[this.highlightedOrderIndex]);
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeOrderDropdown();
    }
  }

  public selectOrder(order: IOrder): void {
    this.isSelectingOrder = true;
    this.deliveryNoteForm.patchValue({ order });
    this.orderSearchValue = this.getOrderLabel(order);
    this.closeOrderDropdown();
  }

  public clearSelectedOrder(): void {
    this.deliveryNoteForm.patchValue({ order: null });
    this.orderSearchValue = '';
    this.orders = [];
    this.filteredOrders = [];
    this.highlightedOrderIndex = -1;
    this.isOrderSearchLoading = false;
    this.closeOrderDropdown();
  }

  private getOrderLabel(order: IOrder): string {
    return `${order.internalOrderId} - ${order.companyName}`;
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    // Ctrl + T shortcut
    if (event.ctrlKey && event.key.toLowerCase() === 't') {
      event.preventDefault();
      this.openTextBlockModal();
    }
  }

  @HostListener('focusin', ['$event'])
  public onFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;

    if (target && (target.tagName === 'TEXTAREA' || (target.tagName === 'INPUT' && target.getAttribute('type') === 'text'))) {
      this.currentFocusedElement = target as HTMLTextAreaElement | HTMLInputElement;
    }
  }

  private openTextBlockModal(): void {
    if (!this.currentFocusedElement) {
      return;
    }

    this.modalService
      .open(PasteTextBlockModalComponent, { type: TextBlockType.LZ })
      .then((textToInsert: string | null) => {
        if (textToInsert && this.currentFocusedElement) {
          this.insertTextAtCursor(textToInsert);
        }
        this.modalService.close();
      })
      .catch(() => {});
  }

  public openTextBlockModalForField(fieldId: string): void {
    // Set the focused element by ID when user klicks the text block icon
    const element = document.getElementById(fieldId) as HTMLTextAreaElement | HTMLInputElement;
    if (element) {
      this.currentFocusedElement = element;
      this.openTextBlockModal();
    }
  }

  public autoResizeNote(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (!target || target.tagName !== 'TEXTAREA') return;

    this.resizeTextarea(target);
  }

  private resizeTextarea(target: HTMLTextAreaElement): void {
    target.style.height = 'auto';
    target.style.height = `${target.scrollHeight}px`;
  }

  private insertTextAtCursor(text: string): void {
    if (!this.currentFocusedElement) return;

    const element = this.currentFocusedElement;
    const currentValue = element.value;

    // Simply append text comma separated to current value
    const newValue = currentValue ? currentValue + ', ' + text : text;

    // Update the form control value
    if (element.id === 'additionalInfo') {
      this.deliveryNoteForm.patchValue({ additionalInfo: newValue });
    } else if (element.id.startsWith('note-')) {
      // Extract position index from element ID (e.g., 'note-0', 'note-1')
      const positionIndex = parseInt(element.id.split('-')[1]);
      const positions = this.deliveryNoteForm.get('positions') as FormArray;

      if (positions && positions.at(positionIndex)) {
        positions.at(positionIndex).patchValue({ note: newValue });
      }
    }

    element.value = newValue;
    if (element.tagName === 'TEXTAREA') {
      this.resizeTextarea(element as HTMLTextAreaElement);
    }
    element.focus();
  }

  public exportPdf(): void {
    if (!this.deliveryNoteId || this.isExporting) return;

    this.isExporting = true;
    this.subscriptions.add(
      this.deliveryNoteService.exportPdf(this.deliveryNoteId).subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const order = this.deliveryNoteForm.get('order')?.value as IOrder | null;
          const orderId = order?.internalOrderId ?? 'unbekannt';
          link.download = `Lieferzettel-${orderId}-${this.noteNumber ?? this.deliveryNoteId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          this.isExporting = false;
        },
        error: () => {
          this.isExporting = false;
        },
      })
    );
  }

  ngOnDestroy(): void {
    const main = document.querySelector('main') as HTMLElement | null;
    if (main) {
      main.style.overflow = '';
    }
    if (this.isEmployeeIdModalOpen) this.modalService.close();
    this.releaseLock();
    this.subscriptions.unsubscribe();
  }
}
